import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import type { Page } from './catalogue';
import { errorMessage } from './errors';

type Cursor = QueryDocumentSnapshot | null;

interface PagedResult<T> {
  key: string;
  items: T[];
  next: Cursor;
  error: string;
}

/**
 * Loads the first page whenever `key` changes and appends pages on `loadMore`.
 * `load` must be memoised by the caller (useCallback) on the same inputs as `key`.
 */
export function usePaged<T>(key: string, load: (after: Cursor) => Promise<Page<T>>) {
  const [result, setResult] = useState<PagedResult<T> | null>(null);
  const [reloads, setReloads] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const fullKey = `${key}#${reloads}`;

  useEffect(() => {
    let cancelled = false;
    load(null).then(
      (page) => !cancelled && setResult({ key: fullKey, items: page.items, next: page.next, error: '' }),
      (err: unknown) => !cancelled && setResult({ key: fullKey, items: [], next: null, error: errorMessage(err) }),
    );
    return () => {
      cancelled = true;
    };
  }, [fullKey, load]);

  const current = result?.key === fullKey ? result : null;

  const loadMore = useCallback(async () => {
    if (!current?.next) return;
    setLoadingMore(true);
    try {
      const page = await load(current.next);
      setResult((r) => r && { ...r, items: [...r.items, ...page.items], next: page.next });
    } catch (err) {
      setResult((r) => r && { ...r, error: errorMessage(err) });
    } finally {
      setLoadingMore(false);
    }
  }, [current, load]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  return {
    loading: current === null,
    items: current?.items ?? [],
    hasMore: Boolean(current?.next),
    error: current?.error ?? '',
    loadingMore,
    loadMore,
    reload,
  };
}

interface ValueResult<T> {
  key: string;
  value: T | null;
  error: string;
}

/** Loads one value whenever `key` changes; `reload` fetches it again. */
export function useLoaded<T>(key: string, load: () => Promise<T>) {
  const [result, setResult] = useState<ValueResult<T> | null>(null);
  const [reloads, setReloads] = useState(0);
  const fullKey = `${key}#${reloads}`;

  useEffect(() => {
    let cancelled = false;
    load().then(
      (value) => !cancelled && setResult({ key: fullKey, value, error: '' }),
      (err: unknown) => !cancelled && setResult({ key: fullKey, value: null, error: errorMessage(err) }),
    );
    return () => {
      cancelled = true;
    };
  }, [fullKey, load]);

  const current = result?.key === fullKey ? result : null;
  const reload = useCallback(() => setReloads((n) => n + 1), []);

  return { loading: current === null, value: current?.value ?? null, error: current?.error ?? '', reload };
}
